import { RefObject, useEffect } from 'react';

import { MouseEventType, TouchEventType } from '@this+that/utils';

type Handler = (event: MouseEvent | TouchEvent) => void;

export interface UseClickOutsideOptions {
  enabled?: boolean;
  excludeRefs?: RefObject<HTMLElement | null>[];
  excludeSelectors?: string[];
  excludePortalIds?: string[];
}

export function useClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  handler: Handler,
  options: UseClickOutsideOptions = {},
) {
  const {
    enabled = true,
    excludeRefs = [],
    excludeSelectors = [],
    excludePortalIds = [],
  } = options;

  useEffect(() => {
    if (!enabled) return;

    const listener = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (ref.current && ref.current.contains(target)) {
        return;
      }

      for (const excludeRef of excludeRefs) {
        if (excludeRef.current && excludeRef.current.contains(target)) {
          return;
        }
      }

      for (const selector of excludeSelectors) {
        if ((target as Element).closest?.(selector)) {
          return;
        }
      }

      for (const portalId of excludePortalIds) {
        const portalElement = document.getElementById(portalId);
        if (portalElement && portalElement.contains(target)) {
          return;
        }
      }

      handler(event);
    };

    document.addEventListener(MouseEventType.MOUSEDOWN, listener);
    document.addEventListener(TouchEventType.TOUCHSTART, listener);

    return () => {
      document.removeEventListener(MouseEventType.MOUSEDOWN, listener);
      document.removeEventListener(TouchEventType.TOUCHSTART, listener);
    };
  }, [ref, handler, enabled, excludeRefs, excludeSelectors, excludePortalIds]);
}

export function useClickOutsideSimple<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  handler: Handler,
  enabled: boolean = true,
) {
  return useClickOutside(ref, handler, { enabled });
}
